import { useState } from 'react';
import * as XLSX from 'xlsx-js-style';

type Section = { key: string; label: string; columns: string[]; rows: (string | number)[][]; note?: string };
type Resp = { ok: boolean; title: string; subtitle: string; from: string; to: string; catalog: Record<string, string>; picked: string[]; sections: Section[]; generated_at: string; queue?: string | null; queues?: string[]; department?: string | null; departments?: string[]; error?: string };

const iso = (d: Date) => d.toISOString().slice(0, 10);
const SHEET_NAMES: Record<string, string> = {
  wxc_summary: 'Calling summary', wxc_monthly: 'Calling monthly', wxcc_summary: 'CC summary',
  wxcc_by_agent: 'CC by agent', wxcc_by_queue: 'CC by queue', wxcc_by_dnis: 'CC by DNIS',
  wxcc_menu: 'IVR menu', wxcc_transfers: 'CC transfers', number_inventory: 'Number inventory',
};

const PLACEHOLDER = `Describe the report you need, for example:

• Number of calls
• Average calls per agent
• Average length of calls
• Where calls were transferred / frequency
• What people select from the phone tree
• Average wait time
• Calls after hours
• Calls abandoned before an agent`;

export default function CdrCustomReport() {
  const today = new Date();
  const [request, setRequest] = useState('');
  const [from, setFrom] = useState(iso(new Date(today.getTime() - 30 * 86400000)));
  const [to, setTo] = useState(iso(today));
  const [d, setD] = useState<Resp | null>(null);
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [queue, setQueue] = useState<string>('');
  const [department, setDepartment] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function generate(sections?: string[], queueOverride?: string, deptOverride?: string) {
    setBusy(true); setErr(null);
    try {
      const dept = deptOverride !== undefined ? deptOverride : department;
      const r = await fetch('/cdr/api/customreport', {
        method: 'POST', credentials: 'include', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ request, from, to, ...(sections ? { sections } : {}), ...(queueOverride !== undefined ? { queue: queueOverride } : {}), ...(dept ? { department: dept } : { department: '' }) }),
      });
      const j = await r.json();
      if (!r.ok || !j.ok) { setErr(j.error || 'Could not generate. Sign in and try again.'); setD(null); return; }
      setD(j);
      setQueue(j.queue || '');
      setDepartment(j.department || '');
      if (j.from) setFrom(j.from);
      if (j.to) setTo(j.to);
      const c: Record<string, boolean> = {};
      Object.keys(j.catalog).forEach(k => { c[k] = (j.picked || []).includes(k); });
      setChecked(c);
    } catch (e: any) { setErr(String(e)); } finally { setBusy(false); }
  }

  function fname(ext: string) {
    return (d?.title || 'Report').replace(/[^\w]+/g, '_').replace(/^_+|_+$/g, '') + '_' + (d?.from || '') + '_to_' + (d?.to || '') + '.' + ext;
  }
  function downloadXlsx() {
    if (!d) return;
    const NAVY = '1F3864', HEAD = '2E5496', BAND = 'F2F2F2', GREY = '595959', LINE = 'BFBFBF';
    const b = { style: 'thin', color: { rgb: LINE } };
    const border = { top: b, bottom: b, left: b, right: b };
    const ST = {
      title: { font: { name: 'Arial', sz: 18, bold: true, color: { rgb: NAVY } } },
      sub: { font: { name: 'Arial', sz: 10, italic: true, color: { rgb: GREY } } },
      meta: { font: { name: 'Arial', sz: 10, color: { rgb: GREY } } },
      metaB: { font: { name: 'Arial', sz: 10, bold: true, color: { rgb: NAVY } } },
      secTitle: { font: { name: 'Arial', sz: 13, bold: true, color: { rgb: NAVY } } },
      header: { font: { name: 'Arial', sz: 11, bold: true, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: HEAD } }, alignment: { horizontal: 'center' }, border },
      cell: { font: { name: 'Arial', sz: 11 }, border },
      band: { font: { name: 'Arial', sz: 11 }, fill: { fgColor: { rgb: BAND } }, border },
      note: { font: { name: 'Arial', sz: 9, italic: true, color: { rgb: '808080' } } },
    } as const;
    const put = (ws: any, r: number, c: number, v: any, s: any) => { const a = XLSX.utils.encode_cell({ r, c }); ws[a] = { t: typeof v === 'number' ? 'n' : 's', v: v ?? '' }; if (s) ws[a].s = s; };
    const widths = (ws: any, cols: string[]) => { ws['!cols'] = cols.map((c, i) => ({ wch: Math.max(i === 0 ? 22 : 12, String(c).length + 2) })); };

    const wb = XLSX.utils.book_new();

    // ---- Overview + Summary ----
    const ov: any = {}; let r = 0;
    put(ov, r++, 0, d.title, ST.title);
    put(ov, r++, 0, d.subtitle, ST.sub);
    put(ov, r, 0, 'Period', ST.metaB); put(ov, r++, 1, d.from + ' to ' + d.to, ST.meta);
    put(ov, r, 0, 'Generated', ST.metaB); put(ov, r++, 1, (d.generated_at || '').replace('T', ' ').slice(0, 16) + ' UTC', ST.meta);
    r++;
    const kpis = d.sections.filter(s => s.columns[0] === 'Metric').flatMap(s => s.rows);
    if (kpis.length) {
      put(ov, r++, 0, 'Summary', ST.secTitle);
      put(ov, r, 0, 'Metric', ST.header); put(ov, r++, 1, 'Value', ST.header);
      kpis.forEach((row, i) => { put(ov, r, 0, row[0], i % 2 ? ST.band : ST.cell); put(ov, r++, 1, row[1], i % 2 ? ST.band : ST.cell); });
      r++;
    }
    put(ov, r++, 0, 'Sections in this report', ST.secTitle);
    d.sections.forEach((s, i) => { put(ov, r, 0, s.label, i % 2 ? ST.band : ST.cell); put(ov, r++, 1, s.rows.length + ' row(s)', i % 2 ? ST.band : ST.cell); });
    ov['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: r + 1, c: 3 } });
    ov['!cols'] = [{ wch: 34 }, { wch: 30 }];
    ov['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 3 } }, { s: { r: 1, c: 0 }, e: { r: 1, c: 3 } }];
    XLSX.utils.book_append_sheet(wb, ov, 'Overview');

    // ---- One styled sheet per section ----
    d.sections.forEach(s => {
      const ws: any = {}; let y = 0;
      put(ws, y++, 0, s.label, ST.secTitle); y++;
      s.columns.forEach((c, ci) => put(ws, y, ci, c, ST.header)); y++;
      s.rows.forEach((row, ri) => { row.forEach((v, ci) => put(ws, y, ci, v, ri % 2 ? ST.band : ST.cell)); y++; });
      if (s.note) { y++; put(ws, y++, 0, s.note, ST.note); }
      ws['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: y + 1, c: Math.max(1, s.columns.length - 1) } });
      widths(ws, s.columns);
      ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: Math.max(1, s.columns.length - 1) } }];
      XLSX.utils.book_append_sheet(wb, ws, (SHEET_NAMES[s.key] || s.key).slice(0, 31));
    });
    XLSX.writeFile(wb, fname('xlsx'));
  }
  function downloadCsv() {
    if (!d) return;
    const esc = (v: any) => { const s = String(v ?? ''); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const lines: string[] = [d.title, d.subtitle, `Period,${d.from} to ${d.to}`, ''];
    d.sections.forEach(s => {
      lines.push(s.label); lines.push(s.columns.map(esc).join(','));
      s.rows.forEach(r => lines.push(r.map(esc).join(',')));
      if (s.note) lines.push(esc(s.note));
      lines.push('');
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = fname('csv'); a.click();
  }

  const anyChecked = Object.values(checked).some(Boolean);

  return (
    <div className="space-y-4">
      <div>
        <div className="text-base font-semibold">Generate custom report</div>
        <div className="text-[11px] text-gray-500">Describe the report you need from Webex Calling (WxC) and Contact Center (WxCC). We build it and you download it.</div>
      </div>

      <div className="card p-3 space-y-2">
        <textarea value={request} onChange={e => setRequest(e.target.value)} rows={7} placeholder={PLACEHOLDER}
          className="w-full text-sm rounded-md border border-tnh-border dark:border-tnh-border-dark bg-transparent px-3 py-2 resize-y" />
        <div className="flex gap-2 items-center flex-wrap">
          <span className="text-xs text-gray-500">From</span>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} className="border rounded-md px-2 py-1 text-xs bg-transparent" />
          <span className="text-xs text-gray-500">To</span>
          <input type="date" value={to} onChange={e => setTo(e.target.value)} className="border rounded-md px-2 py-1 text-xs bg-transparent" />
          <button onClick={() => generate()} disabled={busy || !request.trim()} className="px-3 py-1.5 rounded-md bg-tnh-accent text-white text-xs disabled:opacity-50">{busy ? 'Generating…' : 'Generate report'}</button>
        </div>
        {err && <div className="text-xs text-red-500">{err}</div>}
      </div>

      {d && (<>
        {/* Included sections — confirm/adjust card */}
        <div className="card p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-semibold">Included in this report</div>
            <div className="flex gap-2">
              <button onClick={() => generate(Object.keys(checked).filter(k => checked[k]), queue, department)} disabled={busy || !anyChecked} className="text-[11px] px-2 py-1 rounded bg-gray-100 dark:bg-gray-800 disabled:opacity-50">Update</button>
              <button onClick={downloadXlsx} className="text-[11px] px-2 py-1 rounded bg-tnh-accent text-white">Download Excel</button>
              <button onClick={downloadCsv} className="text-[11px] px-2 py-1 rounded bg-gray-100 dark:bg-gray-800">Download CSV</button>
            </div>
          </div>
          {d.departments && d.departments.length > 0 && (
            <div className="flex items-center gap-2 mb-2 flex-wrap border-b border-tnh-border/40 dark:border-tnh-border-dark/40 pb-2">
              <span className="text-xs font-medium">Department (Calling):</span>
              <select value={department} onChange={e => { const v = e.target.value; setDepartment(v); generate(Object.keys(checked).filter(k => checked[k]), queue, v); }} disabled={busy}
                className="border rounded-md px-2 py-1 text-xs bg-transparent max-w-[240px]">
                <option value="">All departments (whole city)</option>
                {d.departments.map(dep => <option key={dep} value={dep}>{dep}</option>)}
              </select>
              <span className="text-[11px] text-gray-400">{department ? `Webex Calling scoped to “${department}”` : 'Not filtered — pick a department to scope the Calling data'}</span>
            </div>
          )}
          {d.queues && d.queues.length > 0 && (
            <div className="flex items-center gap-2 mb-2 flex-wrap border-b border-tnh-border/40 dark:border-tnh-border-dark/40 pb-2">
              <span className="text-xs font-medium">Contact Center queue:</span>
              <select value={queue} onChange={e => { const v = e.target.value; setQueue(v); generate(Object.keys(checked).filter(k => checked[k]), v); }} disabled={busy}
                className="border rounded-md px-2 py-1 text-xs bg-transparent max-w-[220px]">
                <option value="">All queues (whole contact center)</option>
                {d.queues.map(q => <option key={q} value={q}>{q}</option>)}
              </select>
              <span className="text-[11px] text-gray-400">{queue ? `Filtered to “${queue}” only` : 'Not filtered — pick a queue (e.g. Help Desk) to scope it'}</span>
            </div>
          )}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-1.5">
            {Object.keys(d.catalog).map(k => (
              <label key={k} className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-300 select-none">
                <input type="checkbox" checked={!!checked[k]} onChange={e => setChecked({ ...checked, [k]: e.target.checked })} className="mt-0.5" />
                <span>{d.catalog[k]}</span>
              </label>
            ))}
          </div>
          <div className="text-[11px] text-gray-400 mt-2">Tick or untick sections and press <b>Update</b> to rebuild, then Download.</div>
        </div>

        {/* Report preview */}
        <div className="card p-4">
          <div className="text-lg font-semibold">{d.title}</div>
          <div className="text-xs text-gray-500">{d.subtitle} · {d.from} to {d.to}</div>
          <div className="mt-3 space-y-4">
            {d.sections.map(s => (
              <div key={s.key}>
                <div className="text-sm font-semibold mb-1">{s.label}</div>
                <div className="overflow-auto">
                  <table className="w-full text-xs">
                    <thead className="text-gray-500"><tr>{s.columns.map((c, i) => <th key={i} className={i === 0 ? 'text-left px-2 py-1' : 'text-right px-2 py-1'}>{c}</th>)}</tr></thead>
                    <tbody>
                      {s.rows.length === 0 && <tr><td colSpan={s.columns.length} className="px-2 py-2 text-center text-gray-400">No data in range.</td></tr>}
                      {s.rows.map((r, ri) => <tr key={ri} className="border-t border-gray-100 dark:border-gray-800">{r.map((v, ci) => <td key={ci} className={ci === 0 ? 'text-left px-2 py-1' : 'text-right px-2 py-1'}>{typeof v === 'number' ? v.toLocaleString() : v}</td>)}</tr>)}
                    </tbody>
                  </table>
                </div>
                {s.note && <div className="text-[11px] text-gray-400 mt-1">{s.note}</div>}
              </div>
            ))}
          </div>
        </div>
      </>)}
    </div>
  );
}
